import { type components } from '@/generated/internal-api/openapi-types';
import { _notNil } from '@/littledash';

export type FeatureFlag = components['schemas']['FeatureFlagV1.schema'];

export type FeatureFlagList = components['schemas']['FeatureFlagV1List.schema'];

export type FeatureFlagUpdate = components['schemas']['FeatureFlagUpdateV1List.schema'];

export interface FlagGroup {
  parent: FeatureFlag;
  children: Array<FeatureFlag>;
}

export const partitionFlagsByType = (
  data: Array<FeatureFlag>
): { featureFlags: Array<FeatureFlag>; tenantConfig: Array<FeatureFlag> } => {
  return data.reduce<{ featureFlags: Array<FeatureFlag>; tenantConfig: Array<FeatureFlag> }>(
    (acc, f) => {
      if (f.type === 'feature_flag') {
        acc.featureFlags.push(f);
      } else if (f.type === 'tenant_config') {
        acc.tenantConfig.push(f);
      }
      return acc;
    },
    { featureFlags: [], tenantConfig: [] }
  );
};

export const buildFlagGroups = (flags: Array<FeatureFlag>): Array<FlagGroup> => {
  const { parents, childrenByParent } = flags.reduce<{
    parents: Array<FeatureFlag>;
    childrenByParent: Map<string, Array<FeatureFlag>>;
  }>(
    (acc, flag) => {
      if (_notNil(flag.parent)) {
        const existing = acc.childrenByParent.get(flag.parent) ?? [];
        existing.push(flag);
        acc.childrenByParent.set(flag.parent, existing);
      } else {
        acc.parents.push(flag);
      }
      return acc;
    },
    { parents: [], childrenByParent: new Map() }
  );

  return parents.map((parent) => {
    const children = (childrenByParent.get(parent.name) ?? []).sort((a, b) =>
      a.display_name.localeCompare(b.display_name)
    );
    return { parent, children };
  });
};
